import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useUserInfo, useMerchantConfig, useCurrencyList, type CurrencyItem } from '@/hooks'
import { formatThousands,toFixedNoRound } from '@/utils'
import { Toast, Loading } from 'react-vant'
import Big from 'big.js'
import { APIWeb } from '@/api'
import { getCountryList, type CountryOption } from '@/utils/countryData'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import FixedBottom from '@/components/FixedBottom'
import CurrencySelectPopup from '@/components/CurrencySelectPopup'
import CardView from '../components/card-view'
import Ripple from '@/components/Ripple'
import Divider from '@/components/Divider'

// 卡片类型
type CardType = 'electronic' | 'physical'

export default function ConfirmPayment() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const { userInfo, actions: userActions } = useUserInfo()
    // const { merchantConfig, actions } = useMerchantConfig()

    // HP 余额状态
    const [hpBalance, setHpBalance] = useState('0')

    // HP 币种配置
    const hpCurrency: CurrencyItem = useMemo(() => ({
        key: 'hp',
        name: 'HP',
        symbol: 'HP',
        description: 'HP Token',
        icon: '/images/home/hp.svg',
        balance: hpBalance
    }), [hpBalance])

    // 使用扩展后的币种列表
    const { currencyList, getCurrencyByKey, getBalance } = useCurrencyList({
        extraCurrencies: [hpCurrency]
    })

    // 从 URL 获取参数
    const cardType = (searchParams.get('type') as CardType) || 'electronic'
    const productId = searchParams.get('productId') || ''
    const activationCode = searchParams.get('activationCode') || ''

    // 从 location.state 获取产品数据
    const product = (location.state as { product?: ModelsCardProduct })?.product

    // 获取卡片背景图
    const backgroundImage = useMemo(() => {
        return (product as any)?.image || '/images/card-v2/electronic-card/zise-card-1.png'
    }, [product])

    // 默认币种 key
    const defaultCurrencyKey = currencyList[0]?.key || 'usdt'

    // 选中的币种
    const [selectedCurrency, setSelectedCurrency] = useState<string>(defaultCurrencyKey)
    // 币种选择弹窗
    const [showCurrencyPicker, setShowCurrencyPicker] = useState(false)

    // 邮寄地址相关（仅实体卡需要）
    const [shippingAddressInfo, setShippingAddressInfo] = useState<ShippingAddress | null>(null)
    const [loadingAddress, setLoadingAddress] = useState(false)

    // 国家列表（用于将二字码转换为国家名称显示）
    const [countryList, setCountryList] = useState<CountryOption[]>([])

    useEffect(() => {
        if (cardType === 'physical') {
            getCountryList().then(setCountryList)
        }
    }, [cardType])

    // 根据国家二字码获取国家名称
    const countryLabel = useMemo(() => {
        if (!shippingAddressInfo?.country || countryList.length === 0) return shippingAddressInfo?.country || ''
        const found = countryList.find(c => c.value === shippingAddressInfo.country)
        return found ? found.label : shippingAddressInfo.country
    }, [shippingAddressInfo?.country, countryList])

    // 开卡费用（从产品数据获取，默认0）
    const openFee = useMemo(() => {
        return new Big(product?.open_fee || 0)
    }, [product])

    // 首充金额（从产品数据获取，默认0）
    const minOpenDeposit = useMemo(() => {
        return new Big(product?.min_open_deposit || 0)
    }, [product])

    // 总费用计算
    const totalFee = useMemo(() => {
        // 如果有兑换码，只需首充金额
        if (activationCode) {
            return minOpenDeposit
        }
        // 否则需要开卡费 + 首充金额
        return openFee.plus(minOpenDeposit)
    }, [activationCode, openFee, minOpenDeposit])

    // 获取当前选择币种的信息
    const currentCurrency = useMemo(() => {
        return getCurrencyByKey(selectedCurrency) || currencyList[0]
    }, [selectedCurrency, getCurrencyByKey, currencyList])

    // 获取邮寄地址（仅实体卡需要）
    const fetchShippingAddress = useCallback(async () => {
        if (cardType !== 'physical') return

        try {
            setLoadingAddress(true)
            const response = await APIWeb.getShippingAddress()

            // 判断响应是直接的数组还是包含 data 的对象
            let addressList: ShippingAddress[] = []

            if (Array.isArray(response)) {
                addressList = response
            } else if (response && response.data && Array.isArray(response.data)) {
                addressList = response.data
            }

            // 从数组中取第一条数据
            if (addressList.length > 0) {
                const firstAddress = addressList[0]
                if (firstAddress) {
                    setShippingAddressInfo(firstAddress)
                }
            }
        } catch (error) {
            console.error('获取邮寄地址失败:', error)
        } finally {
            setLoadingAddress(false)
        }
    }, [cardType])

    // 获取 HP 余额
    const fetchHpBalance = useCallback(async () => {
        try {
            const apInfo = await APIWeb.getApDaoInfo()
            setHpBalance(String(apInfo?.hp || 0))
        } catch (error) {
            console.error('获取HP余额失败:', error)
            setHpBalance('0')
        }
    }, [])

    // 页面加载时刷新数据
    useEffect(() => {
        userActions.refreshUserInfo()
        fetchShippingAddress()
        fetchHpBalance()
    }, [fetchShippingAddress, fetchHpBalance])

    // 处理确认支付
    const handleConfirmPayment = useCallback(() => {
        // 使用 hook 获取当前币种余额
        const balance = new Big(getBalance(selectedCurrency) || 0)
        if (balance.lt(totalFee)) {
            Toast.info({
                message: t('余额不足请先充值'),
                position: 'middle'
            })
            return
        }

        // 实体卡需要检查邮寄地址
        if (cardType === 'physical' && !shippingAddressInfo?.id) {
            Toast.info({
                message: t('请填写邮寄地址'),
                position: 'middle'
            })
            return
        }

        // 检查是否绑定邮箱或谷歌验证器
        if (!userInfo?.email && !userInfo?.is_bind_google) {
            Toast.fail(t('请先绑定邮箱或谷歌验证器'))
            return
        }

        // 准备开卡参数（不包含 kyc_id，将在选择持卡人页面添加）
        const applyData = {
            pay_type: selectedCurrency,
            product_id: productId ? Number(productId) : undefined,
            activation_code: activationCode || undefined,
            // 实体卡携带邮寄地址ID
            ...(cardType === 'physical' && shippingAddressInfo?.id && {
                address_id: shippingAddressInfo.id
            })
        }

        // 跳转到选择持卡人页面，传入产品ID用于筛选符合开卡的持卡人
        const selectCardholderUrl = `/card-v2/select-cardholder?type=${cardType}${productId ? `&productId=${productId}` : ''}`
        navigate(selectCardholderUrl, {
            state: { applyData, product }
        })
    }, [selectedCurrency, totalFee, userInfo, t, navigate, cardType, productId, activationCode, shippingAddressInfo, getBalance, product])

    // 打开币种选择弹窗
    const handleOpenCurrencyPicker = useCallback(() => {
        setShowCurrencyPicker(true)
    }, [])

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={<span className="text-base font-medium">{t('申请卡片')}</span>}
            />

            <div className="px-5 pt-4 pb-24">
                {/* 卡片预览 */}
                <CardView
                    cardStatus="pending"
                    backgroundImage={backgroundImage}
                    cardBin={product?.bin}
                />

                {/* 邮寄地址（仅实体卡显示） */}
                {cardType === 'physical' && (
                    <div className="mt-6">
                        {loadingAddress ? (
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-base font-medium text-[#1D2129]">{t('邮寄地址')}</span>
                            </div>
                        ) : shippingAddressInfo ? (
                            // 有地址：标题 + 编辑图标 + 地址详情
                            <>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-base font-medium text-[#1D2129]">{t('邮寄地址')}</span>
                                    <img
                                        src="/icon/address-eidt-v2.svg"
                                        alt="edit"
                                        className="w-5 h-5 cursor-pointer"
                                        onClick={() => navigate('/card-v2/add-shipping-address')}
                                    />
                                </div>
                                <div className="text-sm text-[#667085] leading-relaxed">
                                    <div>{countryLabel}/{shippingAddressInfo.province}/{shippingAddressInfo.city}</div>
                                    <div>{shippingAddressInfo.address_line1}</div>
                                    {shippingAddressInfo.address_line2 && (
                                        <div>{shippingAddressInfo.address_line2}</div>
                                    )}
                                </div>
                            </>
                        ) : (
                            // 无地址：标题 + 编辑图标 + 占位文字
                            <>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-base font-medium text-[#1D2129]">{t('邮寄地址')}</span>
                                    <img
                                        src="/icon/address-eidt-v2.svg"
                                        alt="edit"
                                        className="w-5 h-5 cursor-pointer"
                                        onClick={() => navigate('/card-v2/add-shipping-address')}
                                    />
                                </div>
                                <div className="text-sm text-[#86909C] leading-relaxed">
                                    <div>{t('所在国家/地区/省份/城市')}</div>
                                    <div>{t('详细地址')}</div>
                                </div>
                            </>
                        )}

                        {loadingAddress && (
                            <div className="flex items-center justify-center py-6">
                                <Loading type='ball' />
                            </div>
                        )}
    
                        <Divider type="dashed" className="mt-4" />
                    </div>
                )}

                {/* 支付信息 */}
                <div className="mt-3">
                    {/* 支付账户 */}
                    <div className="flex justify-between items-center py-3">
                        <span className="text-sm text-[#667085]">{t('支付账户')}</span>
                        <Ripple className="rounded-full">
                            <div 
                                className="flex items-center gap-2 cursor-pointer p-2"
                                onClick={handleOpenCurrencyPicker}
                            >
                                <img 
                                    src={currentCurrency?.icon} 
                                    alt={currentCurrency?.name} 
                                    className="w-5 h-5 rounded-full"
                                />
                                <span className="text-sm text-[#1D2129] font-medium">{currentCurrency?.name}</span>
                                <img src="/images/card-v2/select-down.svg" alt="select-down" />
                            </div>
                        </Ripple>
                    </div>

                    {/* 开卡费用（使用兑换码时不显示） */}
                    {!activationCode && (
                        <div className="flex justify-between items-center py-3">
                            <span className="text-sm text-[#6A7187]">{t('开卡费用')}</span>
                            <span className="text-sm text-[#1D2129]">{openFee.toString()} {currentCurrency?.name}</span>
                        </div>
                    )}

                    {/* 首充金额 */}
                    <div className="flex justify-between items-center py-3">
                        <span className="text-sm text-[#6A7187]">{t('首充金额')}</span>
                        <span className="text-sm text-[#1D2129]">{minOpenDeposit.toString()} {currentCurrency?.name}</span>
                    </div>

                    {/* 分隔线 */}
                    <Divider type="dashed" className="my-2" />

                    {/* 总需支付 */}
                    <div className="flex justify-between items-center py-3">
                        <span className="text-sm text-[#6A7187]">{t('总需支付')}</span>
                        <span className="text-xl text-[#1D2129] font-bold">{totalFee.toString()} {currentCurrency?.name}</span>
                    </div>
                </div>
            </div>

            {/* 币种选择弹窗 */}
            <CurrencySelectPopup
                visible={showCurrencyPicker}
                onClose={() => setShowCurrencyPicker(false)}
                onSelect={(currency) => setSelectedCurrency(currency.key || currency.symbol)}
                currencies={currencyList}
                selectedValue={selectedCurrency}
            />

            {/* 固定在底部的确认按钮 */}
            <FixedBottom>
                <CustomButton onClick={handleConfirmPayment}>
                    {t('确认支付')}
                </CustomButton>
            </FixedBottom>
        </PageLayout>
    )
}
